import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { fetchOfferApi } from '@/services/offer-api'
import { trackEvent } from '@/lib/tracking'
import { pagarmeService } from '@/services/pagarme.service'
import {
  formatBRL,
  isValidDocument,
  lookupZipCode,
  OFFER_API,
  OFFER_CHECKOUT_REFUSALS,
  offerTrackingParams,
  type OfferContext,
  type OfferOrder,
  type OfferPayment,
  type OfferPix,
} from '@/lib/oferta'
import { OfferMessage } from './OfferStatus'

const POLL_INTERVAL_MS = 10_000

type Stage = 'details' | 'method' | 'pix' | 'card' | 'paid' | 'refused'

interface Props {
  offerId: string
  /** Link pessoal; sem ele, o WhatsApp lido no QR identifica a pessoa. */
  token: string | null
  phone: string | null
  context: OfferContext & { payment: OfferPayment }
}

interface ApiResult<T> {
  ok: boolean
  status?: string
  data?: T
  message?: string
}

async function call<T>(url: string, init?: RequestInit): Promise<ApiResult<T>> {
  const response = await fetchOfferApi(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init?.headers ?? {}) },
  })
  const body = await response.json().catch(() => null)

  if (response.status === 429) return { ok: false, message: 'Muitas tentativas. Espere um minuto e tente de novo.' }
  if (!response.ok || !body?.success) {
    const fieldError = body?.errors ? (Object.values(body.errors as Record<string, string[]>)[0]?.[0] ?? null) : null
    return { ok: false, message: fieldError ?? body?.message ?? 'Não deu certo. Tente de novo.' }
  }

  return { ok: body.data?.status === 'ok', status: body.data?.status, data: body.data }
}

const inputClass = 'h-12 w-full rounded-xl border border-gray-200 px-3 text-gray-900 focus:border-blue-600 focus:outline-none'

function formatDocument(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14)
  if (digits.length > 11) {
    return digits.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
      .replace(/(\.\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
  }
  return digits.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
    .replace(/(\.\d{3})(\d)/, '$1-$2')
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  )
}

/**
 * Pagamento da oferta na própria página dela, sem login (docs/OFERTAS.md §13).
 * O pedido é amarrado à oferta e a quem respondeu: o PIX vence junto com a
 * oferta e o cartão é conferido na hora da cobrança.
 */
export function OfertaCheckout({ offerId, token, phone, context }: Props) {
  const payment = context.payment
  const [stage, setStage] = useState<Stage>('details')
  const [buyer, setBuyer] = useState({
    name: context.buyer?.name ?? '',
    email: context.buyer?.email ?? '',
    document: '',
    phone: context.buyer?.phone ?? '',
  })
  const [order, setOrder] = useState<OfferOrder | null>(null)
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [pix, setPix] = useState<OfferPix | null>(null)
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '', installments: String(payment.installments) })
  const [address, setAddress] = useState({ zip_code: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const pollRef = useRef<number | null>(null)

  const identity = token ? { t: token } : { phone }

  const stopPolling = () => {
    if (pollRef.current !== null) window.clearInterval(pollRef.current)
    pollRef.current = null
  }

  useEffect(() => stopPolling, [])

  const refuse = (status: string | undefined) => {
    stopPolling()
    setRefusal(OFFER_CHECKOUT_REFUSALS[status ?? ''] ?? 'Não foi possível continuar. Fale com a equipe SLAC.')
    setStage('refused')
  }

  const paid = (latest: OfferOrder) => {
    stopPolling()
    setOrder(latest)
    setStage('paid')
    trackEvent('Purchase', { ...offerTrackingParams(context), value: latest.amount, currency: 'BRL' })
  }

  const watch = (orderId: string) => {
    stopPolling()
    pollRef.current = window.setInterval(async () => {
      try {
        const result = await call<{ order: OfferOrder }>(OFFER_API.order(offerId, orderId))
        const latest = result.data?.order
        if (!latest) return
        setOrder(latest)
        if (latest.payment_status === 'paid') paid(latest)
        if (['expired', 'canceled', 'failed'].includes(latest.payment_status)) {
          stopPolling()
          setPix(null)
          setError('O PIX expirou. Gere um novo se a oferta ainda estiver valendo.')
        }
      } catch {
        // Rede instável na sala: tenta de novo no próximo ciclo.
      }
    }, POLL_INTERVAL_MS)
  }

  const submitDetails = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (buyer.name.trim().split(/\s+/).length < 2) return setError('Informe seu nome completo.')
    if (!/^\S+@\S+\.\S+$/.test(buyer.email.trim())) return setError(payment.grants_access === false ? 'Confira o e-mail: é nele que o comprovante chega.' : 'Confira o e-mail: é nele que o acesso chega.')
    if (!isValidDocument(buyer.document)) return setError('Informe um CPF válido.')

    setBusy(true)
    try {
      const result = await call<{ order: OfferOrder; gateway: { public_key: string | null } }>(OFFER_API.checkout(offerId), {
        method: 'POST',
        body: JSON.stringify({ ...identity, customer: { ...buyer, document: buyer.document.replace(/\D/g, ''), phone: buyer.phone || phone || '' } }),
      })
      if (!result.ok) return result.status ? refuse(result.status) : setError(result.message ?? null)
      setOrder(result.data!.order)
      setPublicKey(result.data!.gateway.public_key)
      setStage('method')
      trackEvent('InitiateCheckout', { ...offerTrackingParams(context), value: payment.price, currency: 'BRL' })
    } catch {
      setError('Sem conexão. Confira a internet e tente de novo.')
    } finally {
      setBusy(false)
    }
  }

  const startPix = async () => {
    if (!order) return
    setError(null)
    setBusy(true)
    try {
      const result = await call<{ payment: { pix: OfferPix }; order: OfferOrder }>(OFFER_API.order(offerId, order.id, 'pix'), { method: 'POST' })
      if (!result.ok) return result.status ? refuse(result.status) : setError(result.message ?? null)
      if (result.data?.order.payment_status === 'failed' || !result.data?.payment.pix?.qr_code_text) {
        return setError('Não foi possível gerar o PIX agora. Tente de novo em instantes.')
      }
      setPix(result.data!.payment.pix)
      setOrder(result.data!.order)
      setStage('pix')
      trackEvent('AddPaymentInfo', { ...offerTrackingParams(context), payment_type: 'pix' })
      watch(order.id)
    } catch {
      setError('Sem conexão. Confira a internet e tente de novo.')
    } finally {
      setBusy(false)
    }
  }

  const payCard = async (event: FormEvent) => {
    event.preventDefault()
    if (!order) return
    setError(null)
    if (!publicKey) return setError('Pagamento com cartão indisponível agora. Use o PIX.')
    if (!pagarmeService.validateCardNumber(card.number)) return setError('Confira o número do cartão.')
    if (address.zip_code.replace(/\D/g, '').length !== 8 || !address.street || !address.number || !address.neighborhood || !address.city || address.state.length !== 2) {
      return setError('Preencha o endereço de cobrança do cartão.')
    }

    setBusy(true)
    try {
      const cardToken = await pagarmeService.tokenizeCard({ card_number: card.number, card_holder_name: card.name, card_expiration_date: card.expiry, card_cvv: card.cvv }, publicKey)
      trackEvent('AddPaymentInfo', { ...offerTrackingParams(context), payment_type: 'credit_card' })
      const result = await call<{ order: OfferOrder }>(OFFER_API.order(offerId, order.id, 'credit-card'), {
        method: 'POST',
        body: JSON.stringify({ card_token: cardToken, installments: Number(card.installments), billing_address: address }),
      })
      if (!result.ok) return result.status ? refuse(result.status) : setError(result.message ?? null)
      const latest = result.data!.order
      if (latest.payment_status === 'paid') paid(latest)
      else {
        setOrder(latest)
        watch(order.id)
        setError('Pagamento em análise. Avisamos por e-mail assim que for aprovado.')
      }
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : 'Não foi possível validar o cartão.')
    } finally {
      setBusy(false)
    }
  }

  const fillFromZip = async (zip: string) => {
    const found = await lookupZipCode(zip)
    if (found) setAddress((current) => ({ ...current, ...found }))
  }

  const copyPix = async () => {
    if (!pix) return
    try {
      await navigator.clipboard.writeText(pix.qr_code_text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      setError('Não deu para copiar. Toque e segure no código para copiar.')
    }
  }

  if (stage === 'paid') {
    return (
      <OfferMessage
        emoji="🎉"
        title={context.first_name ? `Pagamento confirmado, ${context.first_name}!` : 'Pagamento confirmado!'}
        text={
          payment.grants_access === false
            ? `O comprovante de ${payment.product_name} chega em ${buyer.email}, e nossa equipe vai falar com você sobre os próximos passos.`
            : `O acesso a ${payment.product_name} chega em ${buyer.email}. Já pode guardar o celular e voltar para a aula.`
        }
      />
    )
  }

  if (stage === 'refused') {
    return <OfferMessage emoji="⏱️" title={refusal ?? 'Não foi possível continuar'} />
  }

  const installmentOptions = payment.options ?? Array.from({ length: payment.installments }, (_, index) => ({
    installments: index + 1,
    total: payment.price,
    amount: Math.round((payment.price / (index + 1)) * 100) / 100,
  }))

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl bg-blue-50 px-4 py-4">
        <p className="text-sm text-blue-900">{payment.product_name}</p>
        <p className="text-2xl font-bold tabular-nums text-blue-950">{payment.label}</p>
        <p className="text-sm tabular-nums text-blue-900">
          {payment.installments > 1 ? `ou ${formatBRL(payment.price)} à vista` : 'no PIX ou no cartão'}
          {payment.original_price && <span className="ml-2 text-blue-900/60 line-through">{formatBRL(payment.original_price)}</span>}
        </p>
      </div>

      {stage === 'details' && (
        <form onSubmit={submitDetails} className="flex flex-col gap-4">
          <p className="text-gray-700">Seus dados para a matrícula:</p>
          <Field id="oferta-nome" label="Nome completo">
            <input id="oferta-nome" className={inputClass} autoComplete="name" value={buyer.name} onChange={(event) => setBuyer({ ...buyer, name: event.target.value })} />
          </Field>
          <Field id="oferta-email" label="E-mail">
            <input id="oferta-email" type="email" inputMode="email" className={inputClass} autoComplete="email" value={buyer.email} onChange={(event) => setBuyer({ ...buyer, email: event.target.value })} />
          </Field>
          <Field id="oferta-cpf" label="CPF">
            <input id="oferta-cpf" inputMode="numeric" maxLength={18} className={inputClass} autoComplete="off" value={buyer.document} onChange={(event) => setBuyer({ ...buyer, document: formatDocument(event.target.value) })} placeholder="000.000.000-00" />
          </Field>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <Button type="submit" size="lg" className="h-14 bg-blue-600 text-lg text-white hover:bg-blue-700" disabled={busy}>
            {busy ? 'Um instante…' : 'Ir para o pagamento'}
          </Button>
        </form>
      )}

      {stage === 'method' && (
        <div className="flex flex-col gap-3">
          <p className="text-gray-700">Como você quer pagar?</p>
          <Button type="button" size="lg" className="h-14 bg-blue-600 text-lg text-white hover:bg-blue-700" disabled={busy} onClick={startPix}>
            {busy ? 'Gerando o PIX…' : `PIX · ${formatBRL(payment.price)}`}
          </Button>
          <Button type="button" size="lg" variant="outline" className="h-14 text-lg" disabled={busy || !publicKey} onClick={() => setStage('card')}>
            {payment.installments > 1 ? `Cartão em até ${payment.installments}x` : 'Cartão'}
          </Button>
          {!publicKey && <p className="text-sm text-gray-500">Cartão indisponível agora; o PIX funciona normalmente.</p>}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>
      )}

      {stage === 'pix' && pix && (
        <div className="flex flex-col items-center gap-4 text-center">
          {pix.qr_code?.startsWith('http') && (
            <img src={pix.qr_code} alt="QR code do PIX" className="h-56 w-56 rounded-xl border border-gray-200 bg-white" />
          )}
          <p className="text-gray-700">Abra o app do banco, escolha PIX copia e cola e cole o código.</p>
          <Button type="button" size="lg" className="h-14 w-full bg-blue-600 text-lg text-white hover:bg-blue-700" onClick={copyPix}>
            {copied ? 'Código copiado' : 'Copiar código PIX'}
          </Button>
          <p className="break-all rounded-lg bg-gray-50 p-3 text-left font-mono text-xs text-gray-600">{pix.qr_code_text}</p>
          <p className="flex items-center gap-2 text-sm text-gray-500" aria-live="polite">
            <span className="h-2 w-2 animate-pulse rounded-full bg-blue-600" aria-hidden="true" />
            Esperando o pagamento — esta tela atualiza sozinha.
          </p>
          {pix.expires_at && (
            <p className="text-xs text-gray-500">
              Vale até {new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(pix.expires_at))}
            </p>
          )}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>
      )}

      {stage === 'card' && (
        <form onSubmit={payCard} className="flex flex-col gap-4">
          <Field id="cartao-numero" label="Número do cartão">
            <input id="cartao-numero" inputMode="numeric" autoComplete="cc-number" className={inputClass} value={card.number} onChange={(event) => setCard({ ...card, number: event.target.value })} />
          </Field>
          <Field id="cartao-nome" label="Nome impresso no cartão">
            <input id="cartao-nome" autoComplete="cc-name" className={inputClass} value={card.name} onChange={(event) => setCard({ ...card, name: event.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="cartao-validade" label="Validade">
              <input id="cartao-validade" inputMode="numeric" autoComplete="cc-exp" placeholder="MM/AA" className={inputClass} value={card.expiry} onChange={(event) => setCard({ ...card, expiry: event.target.value })} />
            </Field>
            <Field id="cartao-cvv" label="CVV">
              <input id="cartao-cvv" inputMode="numeric" autoComplete="cc-csc" className={inputClass} value={card.cvv} onChange={(event) => setCard({ ...card, cvv: event.target.value })} />
            </Field>
          </div>
          {installmentOptions.length > 1 && (
            <Field id="cartao-parcelas" label="Parcelas">
              <select id="cartao-parcelas" className={inputClass} value={card.installments} onChange={(event) => setCard({ ...card, installments: event.target.value })}>
                {installmentOptions.map((option) => (
                  <option key={option.installments} value={option.installments}>
                    {option.installments === 1 ? `À vista · ${formatBRL(option.total)}` : `${option.installments}x de ${formatBRL(option.amount)}`}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <p className="pt-2 text-sm font-medium text-gray-900">Endereço de cobrança do cartão</p>
          <Field id="cobranca-cep" label="CEP">
            <input
              id="cobranca-cep"
              inputMode="numeric"
              autoComplete="postal-code"
              className={inputClass}
              value={address.zip_code}
              onChange={(event) => {
                setAddress({ ...address, zip_code: event.target.value })
                if (event.target.value.replace(/\D/g, '').length === 8) fillFromZip(event.target.value)
              }}
            />
          </Field>
          <Field id="cobranca-rua" label="Rua">
            <input id="cobranca-rua" autoComplete="address-line1" className={inputClass} value={address.street} onChange={(event) => setAddress({ ...address, street: event.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="cobranca-numero" label="Número">
              <input id="cobranca-numero" className={inputClass} value={address.number} onChange={(event) => setAddress({ ...address, number: event.target.value })} />
            </Field>
            <Field id="cobranca-complemento" label="Complemento">
              <input id="cobranca-complemento" autoComplete="address-line2" className={inputClass} value={address.complement} onChange={(event) => setAddress({ ...address, complement: event.target.value })} />
            </Field>
          </div>
          <Field id="cobranca-bairro" label="Bairro">
            <input id="cobranca-bairro" className={inputClass} value={address.neighborhood} onChange={(event) => setAddress({ ...address, neighborhood: event.target.value })} />
          </Field>
          <div className="grid grid-cols-[1fr_5rem] gap-3">
            <Field id="cobranca-cidade" label="Cidade">
              <input id="cobranca-cidade" autoComplete="address-level2" className={inputClass} value={address.city} onChange={(event) => setAddress({ ...address, city: event.target.value })} />
            </Field>
            <Field id="cobranca-uf" label="UF">
              <input id="cobranca-uf" maxLength={2} autoComplete="address-level1" className={`${inputClass} uppercase`} value={address.state} onChange={(event) => setAddress({ ...address, state: event.target.value.toUpperCase() })} />
            </Field>
          </div>

          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <Button type="submit" size="lg" className="h-14 bg-blue-600 text-lg text-white hover:bg-blue-700" disabled={busy}>
            {busy ? 'Processando…' : `Pagar ${formatBRL(payment.price)}`}
          </Button>
          <button type="button" onClick={() => setStage('method')} className="text-sm text-gray-500 underline underline-offset-4">
            Prefiro PIX
          </button>
        </form>
      )}
    </div>
  )
}
